/* eslint no-param-reassign: ["error", { "props": false }] */
import rootLogger from '@/utils/root-logger';
import fetcher from '@/utils/fetcher';
import {formatUrl} from '@/utils/url-utils';

const logger = rootLogger.create('vuex-modules/recording');

export function vttCueToEvent(cue) {
  return {
    id: cue.id,
    startTime: cue.startTime,
    endTime: cue.endTime,
    payload: JSON.parse(cue.text)
  };
}

const mutations = {
  setSource(state, payload) {
    state.source = payload.source;
  },

  setPlayState(state, payload) {
    state.playState = payload.playState;
  },

  setClickNeeded(state, payload) {
    state.clickNeeded = payload.clickNeeded;
  },

  setPlayedBefore(state, payload) {
    state.playedBefore = payload.playedBefore;
  },

  setContentFile(state, payload) {
    state.contentFile = payload.contentFile;
    state.startPosition = 0;
  },

  setEventFile(state, payload) {
    state.eventFile = payload.eventFile;
  },

  setBrightcoveVideoId(state, payload) {
    state.brightcoveVideoId = payload.brightcoveVideoId;
  },

  setStartPosition(state, payload) {
    state.startPosition = payload.startPosition;
  },

  setRecordingEnded(state, payload) {
    state.recordingEnded = payload.recordingEnded;
  },

  setAllVttCues(state, payload) {
    const events = [];

    for (let index = 0; index < payload.cues.length; index++) {
      const cue = payload.cues[index];
      events.push(vttCueToEvent(cue));
    }

    state.allEvents = events;
  },

  setActiveVttCues(state, payload) {
    const activeEvents = [];

    for (let index = 0; index < payload.activeCues.length; index++) {
      const cue = payload.activeCues[index];
      activeEvents.push(vttCueToEvent(cue));
    }

    state.activeEvents = activeEvents;
  },

  setBlocked(state, payload) {
    if (payload.isBlocked) {
      state.blockedCount += 1;
    } else if (state.blockedCount >= 1) {
      state.blockedCount -= 1;
    }
  },

  setPosition(state, payload) {
    state.position = payload.position;
  },

  setDuration(state, payload) {
    state.duration = payload.duration;
  },

  setTranscriptFile(state, payload) {
    state.transcriptFile = payload.transcriptFile;
  },

  setShowCaptions(state, payload) {
    state.showCaptions = payload.showCaptions
    if(!state.isCaptionsUsedOnce && payload.showCaptions) {
      state.isCaptionsUsedOnce = true;
    }
  },

  setCurrentCaption(state, payload) {
    state.currentCaption = payload.currentCaption
  }
};

const getters = {
  allPollEvents(state) {
    return state.allEvents.filter((evt) => evt.payload.name === 'PollsOrTestsOperation');
  },

  activePoll(state) {
    let poll;
    state.activeEvents.forEach((evt) => {
      if (evt.payload.name === 'PollsOrTestsOperation') {
        poll = evt.payload.data;
      }
    });

    return poll;
  },

  isBlocked(state) {
    return state.blockedCount > 0;
  }
};

const actions = {
  async registerForRecording({ rootGetters }, payload = {}) {
    const operation = 'registerForRecording';
    const { recordingKey, data } = payload;
    logger.log(operation, { recordingKey, data });

    const fetchParams = {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(data)
    };

    const path = `/api/recordings/${recordingKey}/registrants`;
    const url = rootGetters['g2wEnv/globalUrl'](path);
    let registration;

    try {
      registration = await fetcher.fetchJSON(url, fetchParams, { operation, quiet: true });
    } catch (error) {
      if (error.response && error.response.status === 409) {
        registration = await error.response.json();
      } else {
        throw error;
      }
    }

    return registration;
  },

  async getWebinarRecording({ commit, rootGetters }, payload = {}) {
    const { webinarKey, registrantKey, recordingKey } = payload;
    const operation = 'getWebinarRecording';

    let path = `/api/V2/webinars/${webinarKey}`;
    path += `/registrants/${registrantKey}/recordings/${recordingKey}`;
    const url = rootGetters['g2wEnv/globalUrl'](path);

    const recording = await fetcher.fetchJSON(url, {}, {operation});
    commit('setContentFile', { contentFile: recording.downloadUrl });
    return recording;
  },

  async getRecordingAssets({ commit, rootGetters }, payload = {}) {
    const { recordingKey, webinarKey, registrantKey } = payload;
    const assetType = payload.assetType || 'OFFLINEWEBINAR';

    let url = rootGetters['g2wEnv/registrationServiceBaseUrl'];
    url += `/api/v1/webinars/${webinarKey || recordingKey}`;
    url += `/registrants/${registrantKey}/recordingAssets?type=${assetType}`;
    const recordingAssets = await fetcher.fetchJSON(url, {}, { operation: 'getRecordingAssets' });

    let location = formatUrl(recordingAssets.cdnLocation);

    commit('setContentFile', { contentFile: location });

    return {
      recordingAssets,
      location,
      recordingKey: recordingAssets.recordingKey
    };
  }
};

const state = () => ({
  playState: '',
  clickNeeded: null,
  playedBefore: false,
  contentFile: '',
  eventFile: '',
  brightcoveVideoId: '',
  startPosition: 0,
  recordingEnded: false,
  allEvents: [],
  activeEvents: [],
  blockedCount: 0,
  position: 0,
  duration: 0,
  setSource: null,
  transcriptFile: '',
  showCaptions: false,
  currentCaption: '',
  isCaptionsUsedOnce: false,
});

export default {
  namespaced: true,
  getters,
  state,
  mutations,
  actions
};
